import Phaser from 'phaser';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT, RENDER_SCALE } from '../dimensions';
import { tokens } from '../design/tokens';
import { palette } from '../design/palette';
import { gameState } from '../state/GameState';
import { audio } from '../game/Audio';

const PANEL_X = 20;
const PANEL_Y = 80;
const PANEL_W = LOGICAL_WIDTH - 2 * PANEL_X;
const PANEL_H = 480;
const PANEL_RADIUS = 20;
const PANEL_CENTER_X = PANEL_X + PANEL_W / 2;
const MACHINE_Y = PANEL_Y + 200;
const LEVER_Y = PANEL_Y + PANEL_H - 60;
const CLOSE_X_POS = PANEL_X + PANEL_W - 16;
const CLOSE_Y_POS = PANEL_Y + 12;

export interface TokenMachineLaunchData {
  onClose?: () => void;
}

export class TokenMachineScene extends Phaser.Scene {
  private onClose?: () => void;
  private machine!: Phaser.GameObjects.Image;
  private machineScale = 1;
  private idleTween?: Phaser.Tweens.Tween;
  private dispensing = false;

  constructor() { super('TokenMachine'); }

  init(data: TokenMachineLaunchData): void {
    this.onClose = data?.onClose;
    this.dispensing = false;
  }

  create(): void {
    this.cameras.main.setOrigin(0, 0).setZoom(RENDER_SCALE);

    const dim = this.add.rectangle(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    dim.on('pointerdown', () => { if (!this.dispensing) this.dismiss(); });

    // Panel hit area — invisible interactive rect that catches taps inside the
    // panel bounds so they don't fall through to the dim and dismiss.
    this.add.rectangle(PANEL_X, PANEL_Y, PANEL_W, PANEL_H, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();

    // Panel visual: rounded rectangle with subtle stroke.
    const panel = this.add.graphics();
    panel.fillStyle(palette.uiBgChip, 0.95);
    panel.fillRoundedRect(PANEL_X, PANEL_Y, PANEL_W, PANEL_H, PANEL_RADIUS);
    panel.lineStyle(2, palette.uiAccent, 0.5);
    panel.strokeRoundedRect(PANEL_X, PANEL_Y, PANEL_W, PANEL_H, PANEL_RADIUS);

    this.machine = this.add.image(PANEL_CENTER_X, MACHINE_Y, 'token-machine')
      .setOrigin(0.5, 0.5);
    const targetW = 240;
    this.machineScale = targetW / this.machine.width;
    this.machine.setScale(this.machineScale);
    this.machine.setInteractive();

    const breatheMs = tokens.motion.tokenMachine.idleBreatheMs;
    this.idleTween = this.tweens.add({
      targets: this.machine,
      scale: { from: this.machineScale, to: this.machineScale * 1.01 },
      duration: breatheMs / 2,
      yoyo: true,
      repeat: -1,
      ease: tokens.motion.ease.standard
    });

    const lever = this.add.text(PANEL_CENTER_X, LEVER_Y, 'GET TOKENS', {
      fontFamily: tokens.type.ui.family,
      fontSize: `${tokens.type.ui.size + 2}px`,
      color: tokens.colors.ui.accent,
      fontStyle: '700'
    }).setOrigin(0.5).setLetterSpacing(2).setInteractive({ useHandCursor: true });

    lever.on('pointerdown', () => {
      if (this.dispensing) return;
      this.dispensing = true;
      this.dispense(lever);
    });

    const closeX = this.add.text(CLOSE_X_POS, CLOSE_Y_POS, '✕', {
      fontFamily: tokens.type.ui.family,
      fontSize: '22px',
      color: tokens.colors.ui.text
    }).setOrigin(1, 0).setInteractive({ useHandCursor: true });
    closeX.on('pointerdown', () => { if (!this.dispensing) this.dismiss(); });

    this.input.keyboard?.once('keydown-ESC', () => { if (!this.dispensing) this.dismiss(); });

    this.events.once('shutdown', () => {
      this.idleTween?.stop();
      this.idleTween = undefined;
    });
  }

  private dispense(lever: Phaser.GameObjects.Text): void {
    const m = tokens.motion.tokenMachine;

    // Stop idle so squash isn't fighting it.
    this.idleTween?.stop();
    this.idleTween = undefined;

    // Button press-down.
    this.tweens.add({
      targets: lever,
      scale: { from: 1, to: 0.94 },
      duration: 80,
      yoyo: true,
      ease: tokens.motion.ease.emphasized
    });

    // Machine squash-and-stretch.
    this.tweens.add({
      targets: this.machine,
      scaleY: { from: this.machineScale, to: this.machineScale * 0.96 },
      duration: m.leverMs / 3,
      yoyo: true,
      onYoyo: () => audio.lever(),
      onComplete: () => this.machine.setScale(this.machineScale)
    });
    this.tweens.add({
      targets: this.machine,
      x: { from: this.machine.x, to: this.machine.x + 2 },
      duration: 40,
      yoyo: true,
      repeat: 2,
      onComplete: () => { this.machine.x = PANEL_CENTER_X; }
    });

    // Three tokens, staggered.
    for (let i = 0; i < 3; i++) {
      this.time.delayedCall(m.leverMs + i * m.tokenStaggerMs, () => this.spawnToken());
    }

    // Auto-close after the last token finishes.
    const totalMs = m.leverMs + 2 * m.tokenStaggerMs + m.tokenFlightMs + m.tokenFadeMs + m.holdAfterMs;
    this.time.delayedCall(totalMs, () => {
      this.dispensing = false;
      this.dismiss();
    });
  }

  private spawnToken(): void {
    const m = tokens.motion.tokenMachine;

    // Approximate dispense-slot world position (bottom-center of the machine).
    const slotY = this.machine.y + (this.machine.displayHeight / 2) - 20;
    const tok = this.add.image(this.machine.x, slotY, 'token').setOrigin(0.5);
    const targetTokSize = 28; // logical pixels
    tok.setScale(targetTokSize / tok.width);

    const driftX = Phaser.Math.Between(-90, 90);
    const peakY = slotY - 30;
    const endY = LOGICAL_HEIGHT + 40;
    const rotation = Phaser.Math.FloatBetween(-Math.PI * 2, Math.PI * 2);

    // Arc up then down off bottom.
    this.tweens.add({
      targets: tok,
      x: tok.x + driftX,
      y: { from: tok.y, to: peakY },
      duration: m.tokenFlightMs * 0.3,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: tok,
          y: endY,
          duration: m.tokenFlightMs * 0.7,
          ease: 'Sine.easeIn'
        });
      }
    });
    this.tweens.add({ targets: tok, rotation, duration: m.tokenFlightMs });
    this.tweens.add({
      targets: tok,
      alpha: 0,
      delay: m.tokenFlightMs - m.tokenFadeMs,
      duration: m.tokenFadeMs,
      onComplete: () => tok.destroy()
    });

    // Tiny sparkle burst.
    for (let i = 0; i < 4; i++) {
      const sx = tok.x + Phaser.Math.Between(-6, 6);
      const sy = tok.y + Phaser.Math.Between(-4, 4);
      const s = this.add.circle(sx, sy, 2, 0xffe6a8, 1);
      this.tweens.add({
        targets: s,
        y: sy - 18,
        alpha: 0,
        duration: 200,
        ease: 'Cubic.easeOut',
        onComplete: () => s.destroy()
      });
    }

    // Grant + emit + audio, synchronously on spawn frame.
    gameState.wallet.grant(1);
    this.game.events.emit('lavascend:tokens');
    audio.tokenDispense();
  }

  private dismiss(): void {
    audio.panelClose();
    const cb = this.onClose;
    this.onClose = undefined;
    this.scene.stop();
    cb?.();
  }
}
